import { useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  ShieldCheck,
  ShieldAlert,
  FileText,
  ExternalLink,
  Clock,
  CheckCircle2,
  XCircle,
  Fingerprint,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function VerificationView({ user, token, onStatusChange }) {
  const [fbiRef, setFbiRef] = useState("");
  const [notes, setNotes] = useState("");
  const [verification, setVerification] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchStatus();
    // Poll for status changes every 5 seconds (so user sees when admin approves)
    const interval = setInterval(fetchStatus, 5000);
    return () => clearInterval(interval);
  }, []);

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/verification/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setVerification(data.verification);
      if (data.status === "verified") onStatusChange("verified");
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/verification/submit`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ fbi_reference: fbiRef, notes }),
      });
      if (res.ok) {
        onStatusChange("submitted");
        fetchStatus();
      }
    } catch (err) {
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  const status = user?.verification_status || "pending";

  const statusConfig = {
    pending: {
      icon: ShieldAlert,
      color: "#FF2E2E",
      label: "NOT VERIFIED",
      bg: "rgba(255,46,46,0.1)",
    },
    submitted: {
      icon: Clock,
      color: "#FFB800",
      label: "UNDER REVIEW",
      bg: "rgba(255,184,0,0.1)",
    },
    verified: {
      icon: CheckCircle2,
      color: "#00FF66",
      label: "VERIFIED",
      bg: "rgba(0,255,102,0.1)",
    },
    rejected: {
      icon: XCircle,
      color: "#FF2E2E",
      label: "REJECTED",
      bg: "rgba(255,46,46,0.1)",
    },
  };

  const cfg = statusConfig[status] || statusConfig.pending;
  const StatusIcon = cfg.icon;

  return (
    <div className="verification-container scanlines">
      <div className="fingerprint-bg" />

      <div className="relative z-10 max-w-2xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="flex items-center gap-3 mb-2">
          <Fingerprint className="w-6 h-6 text-[#00FF66]" />
          <h2
            className="text-xs uppercase tracking-[0.2em] text-[#00FF66]"
            style={{ fontFamily: "'JetBrains Mono', monospace" }}
          >
            Security Clearance
          </h2>
        </div>
        <h1
          style={{ fontFamily: "'Bodoni Moda', serif" }}
          className="text-4xl sm:text-5xl tracking-tighter text-[#FAFAFA] mb-8"
        >
          FBI Background Check
        </h1>

        {/* Status Badge */}
        <div
          className="p-6 mb-8 border"
          style={{
            background: cfg.bg,
            borderColor: `${cfg.color}30`,
          }}
        >
          <div className="flex items-center gap-3">
            <StatusIcon style={{ color: cfg.color }} className="w-6 h-6" />
            <span
              style={{
                fontFamily: "'JetBrains Mono', monospace",
                color: cfg.color,
              }}
              className="text-sm tracking-widest"
              data-testid="verification-status-badge"
            >
              {cfg.label}
            </span>
          </div>
          {status === "submitted" && (
            <p className="text-[#8A8A93] text-sm mt-3">
              Your verification is being reviewed by our security team. This
              typically takes 24-48 hours.
            </p>
          )}
          {status === "rejected" && (
            <p className="text-[#FF2E2E] text-sm mt-3">
              Your verification was not approved. Please resubmit with valid
              documentation.
            </p>
          )}
        </div>

        {/* FBI Portal Link */}
        <div className="p-6 bg-[#0C0D10] border border-[#1F2027] mb-8">
          <div className="flex items-start gap-3">
            <FileText className="w-5 h-5 text-[#8A8A93] mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="text-[#FAFAFA] text-sm font-medium mb-2">
                Step 1: Obtain Your FBI Identity History Summary
              </h3>
              <p className="text-[#8A8A93] text-sm mb-4 leading-relaxed">
                Visit the FBI's official portal to request your Identity History
                Summary Check. You will receive a reference number upon
                submission.
              </p>
              <a
                href="https://www.edo.cjis.gov/#/do-info"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="fbi-portal-link"
                className="inline-flex items-center gap-2 text-[#00FF66] text-sm hover:text-[#33FF85] transition-colors"
              >
                <ExternalLink size={14} />
                <span
                  style={{ fontFamily: "'JetBrains Mono', monospace" }}
                  className="uppercase tracking-wider"
                >
                  FBI Official Portal
                </span>
              </a>
            </div>
          </div>
        </div>

        {/* Submission Form */}
        {(status === "pending" || status === "rejected") && (
          <div className="p-6 bg-[#0C0D10] border border-[#1F2027]">
            <h3 className="text-[#FAFAFA] text-sm font-medium mb-1">
              Step 2: Submit Your Verification
            </h3>
            <p className="text-[#8A8A93] text-xs mb-6">
              Enter your FBI check reference number below.
            </p>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <Label className="text-[#8A8A93] text-xs uppercase tracking-widest mb-2 block">
                  FBI Reference Number
                </Label>
                <Input
                  data-testid="fbi-reference-input"
                  value={fbiRef}
                  onChange={(e) => setFbiRef(e.target.value)}
                  placeholder="e.g., FBI-2026-XXXXXX"
                  className="bg-[#050505] border-[#1F2027] text-[#FAFAFA] rounded-none focus:ring-1 focus:ring-[#00FF66] focus:border-[#00FF66] h-11"
                  style={{ fontFamily: "'JetBrains Mono', monospace" }}
                  required
                />
              </div>
              <div>
                <Label className="text-[#8A8A93] text-xs uppercase tracking-widest mb-2 block">
                  Additional Notes (Optional)
                </Label>
                <Textarea
                  data-testid="verification-notes-input"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Any additional information..."
                  className="bg-[#050505] border-[#1F2027] text-[#FAFAFA] rounded-none focus:ring-1 focus:ring-[#00FF66] focus:border-[#00FF66] min-h-[80px]"
                />
              </div>
              <Button
                data-testid="submit-verification-btn"
                type="submit"
                disabled={submitting}
                className="w-full bg-[#00FF66] text-black font-semibold rounded-none border border-[#00FF66] hover:bg-[#33FF85] h-11 text-sm uppercase tracking-widest"
              >
                {submitting ? (
                  <span style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                    SUBMITTING...
                  </span>
                ) : (
                  "Submit for Review"
                )}
              </Button>
            </form>
          </div>
        )}

        {status === "submitted" && (
          <div className="p-6 bg-[#0C0D10] border border-[#1F2027] text-center">
            <Clock className="w-10 h-10 text-[#FFB800] mx-auto mb-4" />
            <p
              style={{ fontFamily: "'JetBrains Mono', monospace" }}
              className="text-[#FFB800] text-sm tracking-widest uppercase"
            >
              Verification in progress
            </p>
            <p className="text-[#8A8A93] text-sm mt-2">
              Reference: {verification?.fbi_reference}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
